<template>
    <Hero title="Unified Business Login" :highlight-words="['Single-Sign-On']"
        :subtitle="`Your users log in to your application with the software their company already runs on. Google and Microsoft, plus Salesforce, QuickBooks, and ${AUTH_INTEGRATIONS.length} identity providers. Two minutes to set up, no OAuth2 flows to build. Add SAML SSO when an enterprise customer's security review requires it.`"
        :buttons="[
            {
                link: 'https://app.unified.to',
                title: 'Get started free'
            },
            {
                title: 'See documentation',
                link: 'https://docs.unified.to/auth/login/Sign_in_a_user',
                icon: 'ic:round-keyboard-arrow-right',
            },
        ]" />

    <div class="w-full flex flex-col items-center gap-4">
        <section class="py-12 justify-center flex">
            <img src="/images/unified-sso.png" alt="Unified Business Login"
                class="w-full h-auto max-h-[60vh] object-contain" />
        </section>

        <section class="pt-24 max-w-full">
            <Customers title="Trusted by developers and product teams worldwide" />
        </section>


        <section class="max-w-screen-lg mx-auto w-full py-12 px-6">
            <div class="grid md:grid-cols-2 gap-24 items-start">
                <div class="space-y-6">
                    <h2 class="text-4xl font-bold text-white">Seamless authentication; skip the OAuth flows</h2>
                    <p class="text-neutral-50">
                        Unified removes the complexity of signing-in your users into your application.
                    </p>
                    <p class="text-neutral-50">
                        Redirect your users to one unified authentication URL. They sign-in, you get back their verified email and name. 
                        No tokens, no sessions, no custom OAuth2 flows, no API docs to read. 
                    </p>

                    <a href="https://docs.unified.to/auth/login/Sign_in_a_user"
                        class="text-blue-500 flex items-center gap-2">
                        See documentation
                        <Icon name="ic:round-keyboard-arrow-right" />
                    </a>
                </div>
                <div>
                    <img src="/images/unified-sso-config.png" alt="Authentication configuration"
                        class="w-full h-auto object-contain max-h-96" />
                </div>
            </div>
        </section>

        <section class="max-w-screen-lg mx-auto w-full py-12 px-6">
            <div class="grid md:grid-cols-2 gap-24 items-center">
                <div>
                    <img src="/images/unified-sso.png" alt="Add SAML SSO to your application"
                        class="w-full h-auto object-contain max-h-96" />
                </div>
                <div class="space-y-6">
                    <h2 class="text-4xl font-bold text-white">Add SAML SSO to your application</h2>
                    <p class="text-neutral-50">
                        Let enterprise customers sign in through their own directory. Their IT team controls who gets
                        access, and removes it during offboarding without a request to your support team.
                    </p>
                    <p class="text-neutral-50">
                        Google Workspace Directory, JumpCloud, Microsoft Entra ID, and Okta are supported.
                    </p>
                </div>
            </div>
        </section>

        <section class="px-3 lg:w-full sm:py-12 py-32">
            <UseCases title="Business Login, without the overhead"
                :subtitle="`Login users from ${AUTH_INTEGRATIONS.length} platforms, without building auth flows, handling tokens, or wiring up individual SDKs.`"
                three_col :usecases="[
                    {
                        title: 'Simple auth redirect',
                        body: 'No SDKs - just redirect users to a single URL and receive their verified emails and name in the callback. Sample code provided for React, Vue, and Angular, plus stock provider logos, button colors and names.',
                        // icon: 'heroicons:arrow-path',
                    },
                    {
                        title: 'Comprehensive auth flows',
                        body: 'No OAuth2 handshakes. Unified handles every OAuth2 flow variation, so you don\'t have to. Built on OpenID Connect (OIDC), with standard OAuth2 fallback for providers that don\'t support it.',
                    },
                    // {
                    //     icon: 'heroicons:rectangle-group',
                    //     // image_url: '/icons/magnifying_glass.svg',
                    //     title: 'Pre-built UI Components',
                    //     body: ' Drop in a React, Angular, VueJS, Svelte, or JavaScript component and get instant, frictionless user authorization. Need customization? Access the source code and customize it to fit your brand.'
                    //     // category: 'ATS',
                    //     // button: { link: 'https://unified.to/ats', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    // },
                    // {
                    //     icon: 'heroicons:code-bracket',
                    //     title: 'Comprehensive Auth flows',
                    //     body: 'Unified.to handles all variations of OAuth2 flows, API tokens and other authentication methods, so you can focus on building your product, not authorization.'

                    // },
                    {
                        // icon: 'heroicons:document',
                        title: 'OAuth2 registration guides',
                        body: 'Step-by-step guides to get your own OAuth 2 credentials fast.',

                    },
                    {
                        // icon: 'heroicons:shield-check',
                        title: 'Enterprise-grade security',
                        body: 'SOC 2 compliant. No data storage or caching. Keep API credentials (client secrets, API tokens) locked down in your own secrets manager (AWS, GCP, Azure, or HashiCorp Vault).',
                    },
                    {
                        title: `Authorize across ${AUTH_INTEGRATIONS.length} identity providers`,
                        body: 'From Google and Microsoft to Slack, Salesforce, QuickBooks, and BambooHR. Unified.to supports more identity providers than any other solution.'
                    },
                    {
                        title: 'No access token storage',
                        body: 'Unified.to returns verified identity info, without requiring token handling or session management.',
                    },

                ]" />
        </section>

        <IntegrationsHero title="Social and Business Login that scales with you"
            :subtitle="`Supports ${AUTH_INTEGRATIONS.length} identity providers.`" />


        <!-- <section class="max-w-screen-2xl mx-auto w-full py-12 px-6">
            <div class="mb-12 text-center">
                <h2 class="text-4xl font-bold text-white mb-6">Unified Authorization vs Custom OAuth Builds</h2>
                <p class="text-neutral-50 text-xl max-w-3xl mx-auto">Discover the differences between integration
                    solutions and how Unified.to outpaces alternatives in real-time data access, launch speed,
                    maintenance, and AI usefulness.</p>
            </div>
            <ComparisonTable :columns="AUTH_COLUMNS" :sections="AUTH_COMPARISON" :highlight-column-index="0"
                is-text-left-aligned icon_class="text-xl text-green-500" />
        </section> -->

        <section class="relative flex flex-col justify-center items-center sm:py-12 lg:py-16">
            <div class="h-auto my-15">
                <h2 class="font-semibold">Detailed OAuth2 credential guides</h2>
            </div>

            <div class="flex flex-col gap-8 sm:grid grid-cols-4 mt-8">
                <a target="_blank" :href="guide.button_link" v-for="guide in AUTH_GUIDES" :key="guide.title"
                    class="flex items-center gap-4 border p-8 rounded-lg hover:bg-gray-800 transition-colors">
                    <img :src="guide.image_url" :alt="guide.title" class="w-10 h-10" />
                    <div class="text-lg text-white">{{ guide.title.replace('OAuth2', '') }}
                        <Icon name="ic:round-keyboard-arrow-right" />
                    </div>
                </a>
            </div>
        </section>

        <Testimonials />

        <Hero title="Authenticate. Faster." :highlight-words="['faster.']" :buttons="[
            { link: 'https://app.unified.to', title: 'Get started free' },
            { link: '/contact', title: 'Speak to an expert', icon: 'ic:round-keyboard-arrow-right' },
        ]" />

    </div>
</template>


<script lang="ts">
// import type { IColumnWithKey, IComparisonSection } from '~/components/ComparisonTable.vue';
import { INTEGRATIONS } from '~~/services/integrations';

// export const AUTH_COLUMNS = [
//     {
//         name: 'Unified.to Authorization',
//         // subtitle: 'Extract, Transform, Load',
//         key: 'Unified',
//         isHighlighted: true,
//         // images: ['/logos/Boomi.png', '/logos/Fivetran.png'],
//     },
//     {
//         name: 'Custom-build Authorization',
//         // subtitle: 'Integration Platform as a Service',
//         key: 'Custom',
//         // images: ['/logos/withampersand_logo.jpeg', '/logos/Cyclr.png'],
//     },
//     {
//         name: 'iPass Authorization',
//         // subtitle: 'First Generation',
//         key: 'iPass',
//         // images: ['/logos/Merge.png', '/logos/Kombo.png', '/logos/apideck-white.png'],
//     },
// ] satisfies IColumnWithKey[];


// export const AUTH_COMPARISON: IComparisonSection[] = [
//     {
//         title: '',
//         rows: [
//             {
//                 feature: 'Time to launch',
//                 text: {
//                     Unified: 'Minutes',
//                     Custom: 'Weeks',
//                     iPass: 'Days',
//                 },
//                 // ratings: {
//                 //     Unified: 1,
//                 //     Custom: 5,
//                 //     iPass: 3,
//                 // }
//             },
//             {
//                 feature: 'Customization & White-labeling',
//                 text: {
//                     Unified: 'Yes',
//                     Custom: 'Yes',
//                     iPass: 'Not'
//                 },
//             },
//             {
//                 feature: 'Pre-built UI',
//                 text: {
//                     Unified: 'Yes',
//                     Custom: 'Not',
//                     iPass: 'Yes',
//                 },
//             },
//             {
//                 feature: 'Direct API Option',
//                 text: {
//                     Unified: 'Yes',
//                     Custom: 'Yes',
//                     iPass: 'Not'
//                 },
//             },
//             {
//                 feature: 'Security',
//                 text: {
//                     Unified: 'Yes',
//                     Custom: 'Yes',
//                     iPass: 'Not',
//                 },
//             },
//         ],
//     },
// ] as const;

import { AUTH_GUIDES } from './embeddedauth.vue';
import { setTitle } from '~~/services/common';

export default {
    setup() {
        setTitle(
            'Unified Business Login',
            'Your users log in to your application with the software their company already runs on. Google and Microsoft, plus Salesforce, QuickBooks, and more identity providers. Two minutes to set up, no OAuth2 flows to build.'
        );
        return {
            AUTH_INTEGRATIONS: INTEGRATIONS.filter(i => i.categories?.includes('auth')),
            // AUTH_COLUMNS,
            // AUTH_COMPARISON,
            AUTH_GUIDES
        }
    }
}
</script>